<template>
  <v-sheet
    :class="[
      'imagen-preview',
      'grey lighten-4',
      tamanioGrande ? 'imagen-preview--tamanio-grande' : 'imagen-preview--tamanio-normal',
    ]">
    <div class="preview-container" ref="container">
      <img
        :src="src"
        :style="imageStyle"
        @mousedown="startDrag"
        @wheel.prevent="handleWheel"
        alt="Preview"
        draggable="false" />
    </div>

    <div class="controls">
      <v-btn @click="zoomOut" fab x-small color="white" title="Reducir zoom" elevation="2">
        <v-icon>mdi-minus</v-icon>
      </v-btn>

      <v-btn @click="resetZoom" fab x-small color="white" title="Reset" elevation="2">
        <v-icon>mdi-numeric-0</v-icon>
      </v-btn>

      <v-btn @click="zoomIn" fab x-small color="white" title="Aumentar zoom" elevation="2">
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <v-btn @click="rotateImageRight" fab x-small color="white" title="Rotar imagen" elevation="2">
        <v-icon>mdi-rotate-right</v-icon>
      </v-btn>

      <v-btn @click="rotateImageLeft" fab x-small color="white" title="Rotar imagen izquierda" elevation="2">
        <v-icon>mdi-rotate-left</v-icon>
      </v-btn>

      <v-btn @click="download" fab x-small color="white" title="Descargar imagen" elevation="2">
        <v-icon>mdi-download</v-icon>
      </v-btn>

      <v-btn
        v-if="conDialogo"
        @click="dialog = true"
        fab
        x-small
        color="white"
        title="Abrir en pantalla completa"
        elevation="2">
        <v-icon>mdi-fullscreen</v-icon>
      </v-btn>
    </div>

    <v-dialog v-if="conDialogo" v-model="dialog" hide-overlay max-width="1000" class="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Vista de la imagen</v-toolbar-title>

          <v-spacer></v-spacer>

          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <ImagePreview :src="src" :con-dialogo="false" tamanio-grande />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
export default {
  name: 'ImagenPreview',
  components: {
    ImagePreview: () => import('./ImagenPreview.vue'),
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    nombreArchivo: {
      type: String,
      default: 'documento',
    },
    conDialogo: {
      type: Boolean,
      default: true,
    },
    tamanioGrande: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
      rotation: 0,
      dialog: false,
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        cursor: this.isDragging ? 'grabbing' : 'grab',
        transformOrigin: 'center center',
        rotate: `${this.rotation}deg`,
      };
    },
  },
  methods: {
    zoomIn() {
      this.scale = Math.min(this.scale + 0.2, 5);
    },
    zoomOut() {
      this.scale = Math.max(this.scale - 0.2, 0.5);
    },
    resetZoom() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
    },
    handleWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.scale = Math.max(0.5, Math.min(5, this.scale + delta));
    },
    startDrag(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;

      document.addEventListener('mousemove', this.drag);
      document.addEventListener('mouseup', this.stopDrag);
    },
    drag(e) {
      if (this.isDragging) {
        const deltaX = e.clientX - this.startX;
        const deltaY = e.clientY - this.startY;

        this.startX = e.clientX;
        this.startY = e.clientY;

        // Convertir rotación a radianes y aplicar rotación inversa
        const angle = (-this.rotation * Math.PI) / 180;

        const rotatedDeltaX = deltaX * Math.cos(angle) - deltaY * Math.sin(angle);
        const rotatedDeltaY = deltaX * Math.sin(angle) + deltaY * Math.cos(angle);

        this.translateX += rotatedDeltaX;
        this.translateY += rotatedDeltaY;
      }
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.drag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    rotateImageRight() {
      this.rotation = (this.rotation + 90) % 360;
    },
    rotateImageLeft() {
      this.rotation = (this.rotation - 90 + 360) % 360;
    },
    async obtenerExtensionDeBlob() {
      try {
        const response = await fetch(this.src);
        const blob = await response.blob();
        const mimeType = blob.type;
        const extension = mimeType.split('/')[1];

        return extension;
      } catch (error) {
        window.console.error('Error al obtener el blob:', error);
        return null;
      }
    },
    download() {
      this.obtenerExtensionDeBlob().then((extension) => {
        const link = document.createElement('a');
        link.href = this.src;
        link.download = `${this.nombreArchivo}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('mouseup', this.stopDrag);
  },
};
</script>

<style scoped>
.imagen-preview {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.imagen-preview--tamanio-normal {
  height: 340px;
}

.imagen-preview--tamanio-grande {
  height: calc(100vh - 180px);
}

.preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-container img {
  max-width: 100%;
  max-height: 100%;
  transition: transform 0.1s ease-out;
  user-select: none;
}

.controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  border-radius: 8px;
}
</style>
